import type { Notification, NotificationType } from 'types';
import type { NotificationText } from 'helpers/notification-text';
import {
  Play,
  Square,
  Rocket,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  UserPlus,
  Globe,
  Bell,
  AlertTriangle,
  PlugZap,
  CalendarClock,
  CalendarX,
  CalendarPlus,
  CreditCard,
  Gauge,
  ArrowUpCircle,
  KeyRound,
  ShieldOff,
  Flag,
  FlagOff,
  Wrench,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { NOTIFICATION_TEXT, FALLBACK_NOTIFICATION_TEXT } from 'helpers/notification-text';

type Descriptor = {
  icon: LucideIcon;
  tone: string;
  title: (payload: Record<string, unknown>) => string;
  body?: (payload: Record<string, unknown>) => string;
  link: (notification: Notification) => string | null;
};

/**
 * Icon and colour only.
 *
 * The sentences and the target path come from helpers/notification-text, which the
 * API and the crons also read so an email, a Slack post and this panel cannot
 * disagree about what happened. Keeping presentation here keeps lucide out of a
 * module the server imports.
 *
 * Tones are console tokens, not palette shades: `ok` for a positive or live
 * event, `danger` for a result that costs money, `warn` for something waiting on
 * a decision, `accent` for a deploy or an invitation, `dim` for a plain fact.
 */
const PRESENTATION: Record<NotificationType, { icon: LucideIcon; tone: string }> = {
  'experiment.started': { icon: Play, tone: 'text-ok' },
  'experiment.ended': { icon: Square, tone: 'text-dim' },
  'experiment.deployed': { icon: Rocket, tone: 'text-accent' },
  'experiment.significant.positive': { icon: TrendingUp, tone: 'text-ok' },
  'experiment.significant.negative': { icon: TrendingDown, tone: 'text-danger' },
  'experiment.srm.detected': { icon: AlertTriangle, tone: 'text-warn' },
  'experiment.no_data': { icon: PlugZap, tone: 'text-danger' },
  'experiment.overdue': { icon: CalendarClock, tone: 'text-warn' },
  /**
   * The scheduler wanted to act and would not. Worded as a thing that stopped rather
   * than a thing that failed: the engine declining to start an experiment it cannot
   * trust is it working correctly, and the reason it carries is the whole message.
   */
  'experiment.automation_blocked': { icon: CalendarX, tone: 'text-warn' },
  'implementation.done': { icon: CheckCircle2, tone: 'text-ok' },
  'user.invited': { icon: UserPlus, tone: 'text-accent' },
  'website.created': { icon: Globe, tone: 'text-accent' },
  'billing.mode_changed': { icon: CreditCard, tone: 'text-accent' },
  'billing.trial_extended': { icon: CalendarPlus, tone: 'text-ok' },
  'billing.allowance_warning': { icon: Gauge, tone: 'text-warn' },
  'billing.allowance_reached': { icon: Gauge, tone: 'text-warn' },
  'billing.plan_upgrade_scheduled': { icon: ArrowUpCircle, tone: 'text-accent' },
  'billing.plan_upgraded': { icon: ArrowUpCircle, tone: 'text-accent' },
  'server_key.created': { icon: KeyRound, tone: 'text-accent' },
  'server_key.revoked': { icon: ShieldOff, tone: 'text-warn' },
  'flag.published': { icon: Rocket, tone: 'text-ok' },
  'flag.rollout_advanced': { icon: Flag, tone: 'text-accent' },
  'flag.rollout_done': { icon: CheckCircle2, tone: 'text-ok' },
  'flag.turned_off': { icon: FlagOff, tone: 'text-warn' },
  'flag.implement_reminder': { icon: Wrench, tone: 'text-accent' },
  'flag.guardrail_breach': { icon: ShieldAlert, tone: 'text-danger' },
};

const toDescriptor = (presentation: { icon: LucideIcon; tone: string }, copy: NotificationText): Descriptor => ({
  icon: presentation.icon,
  tone: presentation.tone,
  title: copy.title,
  body: copy.body,
  link: (notification) => copy.path(notification),
});

export const NOTIFICATION_DESCRIPTORS: Record<NotificationType, Descriptor> = Object.fromEntries(
  Object.entries(PRESENTATION).map(([type, presentation]) => [
    type,
    toDescriptor(presentation, NOTIFICATION_TEXT[type as NotificationType]),
  ]),
) as Record<NotificationType, Descriptor>;

export const FALLBACK_DESCRIPTOR: Descriptor = toDescriptor(
  { icon: Bell, tone: 'text-dim' },
  FALLBACK_NOTIFICATION_TEXT,
);

export const describe = (notification: Notification): Descriptor =>
  NOTIFICATION_DESCRIPTORS[notification.type] ?? FALLBACK_DESCRIPTOR;
